import CommentPolicyHint from "../components/CommentPolicyHint";
import { usePermission } from "../hooks/usePermission";
import { useAuth } from "../contexts/AuthContext";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { format } from "date-fns";
import WorkLogTextDisplay from "../components/WorkLogTextDisplay";
import {
  ArrowLeft, Plus, Clock, MessageSquare, Edit2, Trash2,
  X, Send, Timer, Link, ChevronDown, ChevronRight,
  Activity, Users, ExternalLink, FolderKanban
} from "lucide-react";

const fmtMin = (m: number) => { const h = Math.floor(m/60); return `${h}:${String(m%60).padStart(2,'0')}`; };

const parseToMinutes = (val: string, unit: string): number => {
  const s = (val || "").trim();
  const hm = s.match(/^(\d+(?:\.\d+)?)h\s*(\d+)m?$/i);
  if (hm) return Math.max(1, Math.round(parseFloat(hm[1]) * 60 + parseInt(hm[2])));
  const h = s.match(/^(\d+(?:\.\d+)?)h$/i);
  if (h) return Math.max(1, Math.round(parseFloat(h[1]) * 60));
  const m = s.match(/^(\d+)m$/i);
  if (m) return Math.max(1, parseInt(m[1]));
  const n = parseFloat(s);
  if (isNaN(n) || n <= 0) return 0;
  return unit === "hr" ? Math.max(1, Math.round(n * 60)) : Math.max(1, Math.round(n));
};


const API = "";
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const STATUS_COLS = [
  { key:"OPEN",        label:"To Do",       bg:"bg-gray-50",   badge:"bg-gray-100 text-gray-700",   dot:"bg-gray-400"  },
  { key:"IN_PROGRESS", label:"In Progress", bg:"bg-blue-50",   badge:"bg-blue-100 text-blue-700",   dot:"bg-blue-500"  },
  { key:"ON_HOLD",     label:"On Hold",     bg:"bg-orange-50", badge:"bg-orange-100 text-orange-700",dot:"bg-orange-400"},
  { key:"COMPLETED",   label:"Completed",   bg:"bg-green-50",  badge:"bg-green-100 text-green-700", dot:"bg-green-500" },
];
const sbadge = (s:string) => STATUS_COLS.find(c=>c.key===s)?.badge || "bg-gray-100 text-gray-700";
const sdot   = (s:string) => STATUS_COLS.find(c=>c.key===s)?.dot   || "bg-gray-400";
const PSBADGE:any={PLANNING:"bg-purple-100 text-purple-700",IN_PROGRESS:"bg-blue-100 text-blue-700",ON_HOLD:"bg-orange-100 text-orange-700",COMPLETED:"bg-green-100 text-green-700",CANCELLED:"bg-red-100 text-red-700"};

type Panel = { type:"task"|"subtask"; id:string } | null;

export default function ProjectDetail() {
  const { id } = useParams<{id:string}>();
  const nav = useNavigate();
  const { isAdmin, user } = useAuth();
  const isManagerOrAdmin = isAdmin || user?.role === "MANAGER" || user?.role === "DEPARTMENT_HEAD";
  const canEdit   = usePermission("projects", "edit");
  const [classifyingStep, setClassifyingStep] = useState(false);
  const classifyStep = async (taskId: string, level: string) => {
    setClassifyingStep(true);
    try {
      await axios.put(`${API}/api/tasks/${taskId}/classify`, { workLevel: level }, { headers: hdr() });
      toast.success(`Classified as ${level}`);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed");
    } finally { setClassifyingStep(false); }
  };
  const canDelete = usePermission("projects", "delete");
  const [project, setProject]   = useState<any>(null);
  const [loading, setLoading]   = useState(true);
  const [users, setUsers]       = useState<any[]>([]);
  const [searchParams] = useSearchParams();
  const initView = searchParams.get("view");
  const initTab = initView === "list" ? "list" : initView === "activity" ? "activity" : "kanban";
  const [activeTab, setActiveTab] = useState<"kanban"|"list"|"activity">(initTab as any);
  const [panel, setPanel]       = useState<Panel>(null);  // right panel
  const [expandedST, setExpandedST] = useState<string|null>(null); // expanded subtask in panel

  // modals
  const [modal, setModal] = useState<string|null>(null); // "task"|"subtask"|"log-proj"|"log-task"|"log-sub"
  const [editItem, setEditItem] = useState<any>(null);

  // forms
  const blankTask = { title:"", description:"", assignedToId:"", status:"OPEN", referenceLink:"" };
  const blankSub  = { title:"", description:"", assignedToId:"", status:"OPEN", referenceLink:"" };
  const blankLog  = { minutes:"", unit:"min", comment:"", date:format(new Date(),"yyyy-MM-dd"), employeeId:"" };
  const [descExpanded, setDescExpanded] = useState(false);
  const [taskForm, setTaskForm] = useState({...blankTask});
  const [subForm,  setSubForm]  = useState({...blankSub});
  const [logForm,  setLogForm]  = useState({...blankLog});
  const [projComment, setProjComment] = useState("");
  const [taskComment, setTaskComment] = useState("");
  const [subComment,  setSubComment]  = useState("");

  const dragRef = useRef<string|null>(null);

  useEffect(()=>{
    load();
    axios.get(`${API}/api/users`,{headers:hdr()}).then(r=>setUsers(r.data)).catch(()=>{});
  },[id]);

  
  const toggleLock = async () => {
    if (!project) return;
    try {
      await axios.put(`${API}/api/projects/${project.id}/lock`, {}, {headers: hdr()});
      toast.success(project.isLocked ? "Project unlocked" : "Project locked");
      load();
    } catch { toast.error("Failed to toggle lock"); }
  };
  const load = async() => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/projects/${id}`,{headers:hdr()});
      setProject(r.data);
      // refresh panel data
      if(panel?.type==="task"){
        const t = r.data.tasks?.find((t:any)=>t.id===panel.id);
        if(!t) setPanel(null);
      }
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  const panelTask    = panel?.type==="task"    ? project?.tasks?.find((t:any)=>t.id===panel.id)   : null;
  const panelSubTask = panel?.type==="subtask" && panelTask
    ? panelTask.subTasks?.find((s:any)=>s.id===panel.id) : null;

  // ── Task CRUD ────────────────────────────────────────────────────────────────
  const submitTask = async(e:React.FormEvent)=>{
    e.preventDefault();
    try {
      const data:any = {...taskForm, projectId: id};
      if(!data.assignedToId) delete data.assignedToId;
      if(!data.referenceLink) delete data.referenceLink;
      if(editItem) {
        await axios.put(`${API}/api/tasks/${editItem.id}`,data,{headers:hdr()});
        toast.success("Step updated");
      } else {
        await axios.post(`${API}/api/tasks`,data,{headers:hdr()});
        toast.success("Step created");
      }
      closeModal(); load();
    } catch { toast.error("Failed to save step"); }
  };
  const deleteTask = async(tid:string)=>{
    if(!window.confirm("Delete this step?")) return;
    try { await axios.delete(`${API}/api/tasks/${tid}`,{headers:hdr()}); toast.success("Deleted"); if(panel?.id===tid) setPanel(null); load(); }
    catch { toast.error("Failed"); }
  };
  const moveTask = async(tid:string, status:string)=>{
    try { await axios.put(`${API}/api/tasks/${tid}`,{status},{headers:hdr()}); load(); }
    catch { toast.error("Failed to move"); }
  };

  // ── SubTask CRUD ─────────────────────────────────────────────────────────────
  const submitSubTask = async(e:React.FormEvent)=>{
    e.preventDefault();
    if(!panelTask) return;
    try {
      const data:any = {...subForm};
      if(!data.assignedToId) delete data.assignedToId;
      if(!data.referenceLink) delete data.referenceLink;
      if(editItem?.type==="subtask") {
        await axios.put(`${API}/api/tasks/subtasks/${editItem.id}`,data,{headers:hdr()});
        toast.success("Sub-step updated");
      } else {
        await axios.post(`${API}/api/tasks/${panelTask.id}/subtasks`,data,{headers:hdr()});
        toast.success("Sub-step created");
      }
      closeModal(); load();
    } catch { toast.error("Failed to save sub-step"); }
  };
  const deleteSubTask = async(sid:string)=>{
    if(!window.confirm("Delete this sub-step?")) return;
    try { await axios.delete(`${API}/api/projects/${id}/subtasks/${sid}`,{headers:hdr()}); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  // ── Work Logs ────────────────────────────────────────────────────────────────
  const submitLog = async(e:React.FormEvent)=>{
    e.preventDefault();
    try {
      const mins = parseToMinutes(logForm.minutes, logForm.unit || "min");
      if (!mins || mins < 1) { toast.error("Minimum 1 minute"); return; }
      // Smart comment policy check
      try {
        const pc = await axios.get(`${API}/api/comment-policy/check?minutes=${mins}`, { headers: hdr() });
        const v = pc.data || {};
        const trimmed = (logForm.comment || "").trim();
        if (v.required && (!trimmed || trimmed.length < (v.minLength || 0))) {
          toast.error(`Comment of ${v.minLength}+ chars required for ${mins}m`);
          return;
        }
        // Recommended: warn once, allow continuing
        if (!v.required && v.recommended && !trimmed) {
          if (!window.confirm(`You worked ${mins} minutes — a short comment is recommended. Save without one?`)) {
            return;
          }
        }
      } catch(_e) {}
      const d = {...logForm, minutes: mins};
      if(modal==="log-proj") {
        if(editItem) { /* project log edit not yet */ }
        else await axios.post(`${API}/api/projects/${id}/worklogs`,d,{headers:hdr()});
      } else if(modal==="log-task" && panelTask) {
        if(editItem) await axios.put(`${API}/api/tasks/${panelTask.id}/work-logs/${editItem.id}`,d,{headers:hdr()}).catch(()=>{ throw new Error(); });
        else await axios.post(`${API}/api/tasks/${panelTask.id}/work-logs`,d,{headers:hdr()});
      } else if(modal==="log-sub" && panelTask) {
        const sid = editItem?.subTaskId || expandedST;
        if(editItem) await axios.put(`${API}/api/projects/${id}/subtasks/${sid}/worklogs/${editItem.id}`,d,{headers:hdr()});
        else await axios.post(`${API}/api/projects/${id}/subtasks/${sid}/worklogs`,d,{headers:hdr()});
      }
      toast.success("Time logged"); closeModal(); load();
    } catch (err: any) { toast.error(err?.response?.data?.message || "Failed to log time"); }
  };
  const deleteLog = async(type:"proj"|"task"|"sub", logId:string, extraId?:string)=>{
    if(!window.confirm("Delete time log?")) return;
    try {
      if(type==="proj") await axios.delete(`${API}/api/projects/${id}/worklogs/${logId}`,{headers:hdr()});
      else if(type==="task" && panelTask) await axios.delete(`${API}/api/tasks/${panelTask.id}/work-logs/${logId}`,{headers:hdr()}).catch(()=>{ throw new Error("No task log delete endpoint"); });
      else if(type==="sub" && extraId) await axios.delete(`${API}/api/projects/${id}/subtasks/${extraId}/worklogs/${logId}`,{headers:hdr()});
      toast.success("Deleted"); load();
    } catch { toast.error("Failed"); }
  };

  // ── Comments ─────────────────────────────────────────────────────────────────
  const addProjComment = async()=>{
    if(!projComment.trim()) return;
    try { await axios.post(`${API}/api/projects/${id}/comments`,{content:projComment},{headers:hdr()}); setProjComment(""); load(); }
    catch { toast.error("Failed"); }
  };
  const addTaskComment = async()=>{
    if(!taskComment.trim() || !panelTask) return;
    try { await axios.post(`${API}/api/tasks/${panelTask.id}/comments`,{content:taskComment},{headers:hdr()}); setTaskComment(""); load(); }
    catch { toast.error("Failed"); }
  };
  const addSubComment = async(sid:string)=>{
    if(!subComment.trim()) return;
    try { await axios.post(`${API}/api/projects/${id}/subtasks/${sid}/comments`,{content:subComment},{headers:hdr()}); setSubComment(""); load(); }
    catch { toast.error("Failed"); }
  };

  // ── Drag & Drop ──────────────────────────────────────────────────────────────
  const onDrop = (e:React.DragEvent,status:string)=>{ e.preventDefault(); if(dragRef.current) moveTask(dragRef.current,status); dragRef.current=null; };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const openModal = (type:string, item?:any)=>{ setEditItem(item||null); setModal(type);
    if(type==="task")    setTaskForm(item ? {title:item.title,description:item.description||"",assignedToId:item.assignedToId||"",status:item.status,referenceLink:item.referenceLink||""} : {...blankTask});
    if(type==="subtask") setSubForm(item  ? {title:item.title,description:item.description||"",assignedToId:item.assignedToId||"",status:item.status,referenceLink:item.referenceLink||""} : {...blankSub});
    if(type==="log-proj"||type==="log-task"||type==="log-sub") setLogForm(item ? {minutes:String(item.minutes),unit:"min",comment:item.comment||"",date:format(new Date(item.date),"yyyy-MM-dd"),employeeId:item.employeeId||""} : {...blankLog});
  };
  const closeModal = ()=>{ setModal(null); setEditItem(null); };
  const tasksByStatus = (s:string) => (project?.tasks||[]).filter((t:any)=>t.status===s);
  const totalProjHours = ()=>{
    const directMins = (project?.projectWorkLogs||[]).reduce((s:number,l:any)=>s+(l.minutes||0),0);
    const taskMins = (project?.tasks||[]).reduce((s:number,t:any)=>s+(t.totalLoggedMinutes||0),0);
    return fmtMin(directMins+taskMins);
  };

  if(loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"/></div>;
  if(!project) return <div className="p-6 text-red-500">Project not found</div>;

  return (
    // OPTION_C_APPLIED
    <div className="flex">
      {/* ── Main ── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Header */}
        <div className="bg-white border-b px-6 py-4 flex items-center gap-4 sticky top-0 z-30">
          <button onClick={()=>nav("/projects")} className="text-gray-500 hover:text-gray-700"><ArrowLeft size={20}/></button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-gray-900 truncate">{project.name}</h1>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${PSBADGE[project.status]}`}>{project.status}</span>
            </div>
            {project.clientName && <p className="text-xs text-gray-400">Client: {project.clientName}</p>}
          </div>
          {/* Stats */}
          <div className="flex gap-5 text-sm flex-shrink-0">
            <div className="text-center"><p className="font-bold text-gray-900">{(project.tasks||[]).length}</p><p className="text-xs text-gray-400">Steps</p></div>
            <div className="text-center"><p className="font-bold text-blue-600">{totalProjHours()}h</p><p className="text-xs text-gray-400">Total Hours</p></div>
            {project.estimatedHours && <div className="text-center"><p className="font-bold text-gray-600">{project.estimatedHours}h</p><p className="text-xs text-gray-400">Estimated</p></div>}
            {project.budget && <div className="text-center"><p className="font-bold text-green-600">${project.budget?.toLocaleString()}</p><p className="text-xs text-gray-400">Budget</p></div>}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={()=>openModal("log-proj")} className="flex items-center gap-1 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm hover:bg-gray-50">
              <Timer size={15}/> Log Time
            </button>
            <button onClick={()=>openModal("task")} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700">
              <Plus size={15}/> Add Step
            </button>
          </div>
        </div>

        {/* Project Description Banner */}
        {project.description && (
          <div className="bg-gray-50 border-b px-6 py-3">
            <div className="flex items-start gap-2">
              <span className="text-gray-400 shrink-0 text-sm leading-6">📄</span>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Project Description</p>
                <p
                  className={`text-sm text-gray-700 whitespace-pre-line break-words ${descExpanded ? "" : "line-clamp-3"}`}
                >{project.description}</p>
                {project.description.length > 180 && (
                  <button
                    type="button"
                    onClick={() => setDescExpanded(e => !e)}
                    className="text-xs text-blue-600 hover:text-blue-700 mt-1 font-medium"
                  >
                    {descExpanded ? "Show less" : "Show more"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Team */}
        <div className="bg-white border-b px-6 py-2 flex items-center gap-3">
          <Users size={14} className="text-gray-400"/>
          {(project.members||[]).map((m:any)=>(
            <div key={m.id} className="flex items-center gap-1 bg-gray-100 rounded-full px-2.5 py-1">
              <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-xs text-white font-bold">{m.user.name[0]}</div>
              <span className="text-xs text-gray-700">{m.user.name}</span>
            </div>
          ))}
          {(project.members||[]).length===0 && <span className="text-xs text-gray-400">No members</span>}
        </div>

        {/* Tabs */}
        <div className="bg-white border-b px-6 flex gap-6 sticky top-[73px] z-20">
          {(["kanban","list","activity"] as const).map(t=>(
            <button key={t} onClick={()=>setActiveTab(t)}
              className={`py-3 text-sm font-medium border-b-2 capitalize ${activeTab===t?"border-blue-600 text-blue-600":"border-transparent text-gray-500 hover:text-gray-700"}`}>
              {t==="kanban"?"Kanban Board":t==="list"?"Step List":"Activity"}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-5">

          {/* KANBAN */}
          {activeTab==="kanban" && (
            <div className="grid grid-cols-4 gap-4">
              {STATUS_COLS.map(col=>(
                <div key={col.key} className="flex flex-col"
                  onDragOver={e=>e.preventDefault()} onDrop={e=>onDrop(e,col.key)}>
                  <div className={`flex items-center justify-between px-3 py-2 rounded-t-lg ${col.badge}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${col.dot}`}/>
                      <span className="text-sm font-semibold">{col.label}</span>
                    </div>
                    <span className="text-xs bg-white/70 rounded-full px-2 py-0.5 font-medium">{tasksByStatus(col.key).length}</span>
                  </div>
                  <div className="flex-1 p-2 space-y-2 min-h-40 rounded-b-lg border-2 border-dashed border-gray-200 bg-gray-50/50">
                    {tasksByStatus(col.key).map((task:any)=>(
                      <div key={task.id} draggable
                        onDragStart={e=>{dragRef.current=task.id; e.dataTransfer.effectAllowed="move";}}
                        onClick={()=>setPanel({type:"task",id:task.id})}
                        className={`bg-white rounded-xl p-3 shadow-sm border cursor-pointer hover:shadow-md transition-all select-none ${panel?.id===task.id?"border-blue-400":"border-gray-200 hover:border-blue-200"}`}>
                        {/* Card header */}
                        <div className="flex items-start justify-between gap-1 mb-2">
                          <p className="text-sm font-semibold text-gray-800 line-clamp-2 flex-1">{task.title}</p>
                          <div className="flex gap-0.5 flex-shrink-0">
                            <button onClick={e=>{e.stopPropagation();openModal("task",{...task,type:"task"});setEditItem(task);}}
                              className="p-1 text-gray-300 hover:text-blue-500 rounded"><Edit2 size={12}/></button>
                            <button onClick={e=>{e.stopPropagation();deleteTask(task.id);}}
                              className="p-1 text-gray-300 hover:text-red-500 rounded"><Trash2 size={12}/></button>
                          </div>
                        </div>
                        {/* Assigned */}
                        {task.assignedTo && (
                          <div className="flex items-center gap-1 mb-2">
                            <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                              <span className="text-xs text-purple-700 font-bold">{task.assignedTo.name[0]}</span>
                            </div>
                            <span className="text-xs text-gray-500">{task.assignedTo.name}</span>
                          </div>
                        )}
                        {/* Footer stats */}
                        <div className="flex items-center justify-between text-xs text-gray-400 mt-2">
                          <div className="flex items-center gap-1"><Clock size={11}/><span>{fmtMin(task.totalLoggedMinutes||0)}</span></div>
                          <div className="flex items-center gap-2">
                            {task.referenceLink && <Link size={11} className="text-blue-400"/>}
                            <div className="flex items-center gap-1"><MessageSquare size={11}/>{(task._count?.comments||task.comments?.length||0)}</div>
                            {(task.subTasks||[]).length>0 && (
                              <div className="flex items-center gap-1">
                                <FolderKanban size={11}/>
                                <span>{(task.subTasks||[]).filter((s:any)=>s.status==="COMPLETED").length}/{(task.subTasks||[]).length}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {tasksByStatus(col.key).length===0 && (
                      <div className="flex items-center justify-center h-20 text-gray-300 text-xs">Drop here</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* LIST */}
          {activeTab==="list" && (
            <div className="space-y-2">
              {(project.tasks||[]).length===0 ? (
                <div className="text-center py-16 text-gray-400"><FolderKanban size={40} className="mx-auto mb-2"/><p>No steps yet</p></div>
              ) : (project.tasks||[]).map((task:any)=>(
                <div key={task.id} className={`bg-white rounded-xl border p-4 cursor-pointer hover:shadow-sm transition-all ${panel?.id===task.id?"border-blue-400":"border-gray-200"}`}
                  onClick={()=>setPanel({type:"task",id:task.id})}>
                  <div className="flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sdot(task.status)}`}/>
                    <p className="flex-1 font-medium text-gray-800">{task.title}</p>
                    {task.assignedTo && <span className="text-xs text-gray-400">{task.assignedTo.name}</span>}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sbadge(task.status)}`}>{task.status}</span>
                    <div className="flex items-center gap-1 text-xs text-gray-400"><Clock size={12}/>{fmtMin(task.totalLoggedMinutes||0)}</div>
                    {task.referenceLink && <a href={task.referenceLink} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} className="text-blue-400 hover:text-blue-600"><ExternalLink size={14}/></a>}
                  </div>
                  {(task.subTasks||[]).length>0 && (
                    <div className="mt-2 ml-5 flex gap-1 flex-wrap">
                      {(task.subTasks||[]).map((s:any)=>(
                        <span key={s.id} className={`text-xs px-2 py-0.5 rounded-full ${sbadge(s.status)}`}>{s.title}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ACTIVITY */}
          {activeTab==="activity" && (
            <div className="space-y-6 max-w-2xl">
              {/* Project Time Logs */}
              {(project.projectWorkLogs||[]).length>0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Clock size={14} className="text-gray-400"/>
                    <span className="text-sm font-semibold text-gray-600">Project Time Logs</span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {(project.projectWorkLogs||[]).map((l:any)=>(
                      <div key={l.id} className="w-full bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Clock size={12} className="text-blue-500"/>
                          <span className="font-semibold text-blue-700">{fmtMin(l.minutes||0)}</span>
                          <span className="text-gray-700">{l.employee?.name}</span>
                          <span className="text-gray-400 ml-auto">{format(new Date(l.date),"MMM dd")}</span>
                          <button onClick={()=>deleteLog("proj",l.id)} className="text-gray-300 hover:text-red-500"><Trash2 size={11}/></button>
                        </div>
                        <WorkLogTextDisplay comment={l.comment} description={l.description} compact />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Project Comments */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare size={14} className="text-gray-400"/>
                  <span className="text-sm font-semibold text-gray-600">Project Comments</span>
                </div>
                <div className="space-y-2 mb-2">
                  {(project.comments||[]).map((c:any)=>(
                    <div key={c.id} className="flex gap-2 items-start">
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-blue-700">{c.author?.name?.[0]}</span>
                      </div>
                      <div className="flex-1 bg-gray-50 rounded-lg px-3 py-1.5">
                        <span className="text-xs font-semibold text-gray-700">{c.author?.name}: </span>
                        <span className="text-xs text-gray-600">{c.content}</span>
                        <span className="text-xs text-gray-300 ml-2">{format(new Date(c.createdAt),"MMM dd HH:mm")}</span>
                      </div>
                    </div>
                  ))}
                  {(project.comments||[]).length===0 && <p className="text-xs text-gray-300">No comments yet</p>}
                </div>
                <div className="flex gap-2">
                  <input className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Add project comment..." value={projComment}
                    onChange={e=>setProjComment(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addProjComment()}/>
                  <button onClick={addProjComment} className="bg-blue-600 text-white px-4 rounded-lg hover:bg-blue-700"><Send size={15}/></button>
                </div>
              </div>

              {/* Activity Log */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Activity size={14} className="text-gray-400"/>
                  <span className="text-sm font-semibold text-gray-600">Activity</span>
                </div>
                <div className="space-y-3">
              {(project.activityLogs||[]).length===0 ? (
                <div className="text-center py-16 text-gray-400"><Activity size={40} className="mx-auto mb-2"/><p>No activity</p></div>
              ) : (project.activityLogs||[]).map((a:any)=>(
                <div key={a.id} className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-blue-700">{(a.userName||a.user?.name||"?")[0]}</span>
                  </div>
                  <div className="flex-1 bg-white rounded-xl border border-gray-100 px-4 py-2">
                    <p className="text-sm text-gray-800"><span className="font-medium">{a.userName||a.user?.name}</span> {a.action}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{format(new Date(a.createdAt),"MMM dd, HH:mm")}</p>
                  </div>
                </div>
              ))}
                </div>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* ── Right Panel ── */}
      {panel && (panelTask||panelSubTask) && (
        <div className="w-96 bg-white border-l border-gray-200 flex flex-col flex-shrink-0 sticky top-0 self-start max-h-screen overflow-y-auto">
          {/* Panel header */}
          <div className="px-4 py-3 border-b flex items-center justify-between bg-gray-50">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {panel.type==="subtask" && (
                <button onClick={()=>setPanel({type:"task",id:panelTask?.id})} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                  <ChevronRight size={16}/>
                </button>
              )}
              <h3 className="font-semibold text-gray-800 text-sm truncate">
                {panel.type==="task" ? panelTask?.title : panelSubTask?.title}
              </h3>
            </div>
            <button onClick={()=>setPanel(null)} className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-2"><X size={18}/></button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* TASK PANEL */}
            {panel.type==="task" && panelTask && (
              <div>
                {/* Task info */}
                <div className="p-4 border-b space-y-3">
                  {panelTask.description && <p className="text-sm text-gray-600">{panelTask.description}</p>}
                  <div className="flex flex-wrap gap-2">
                    <select value={panelTask.status}
                      onChange={async e=>{ await moveTask(panelTask.id,e.target.value); }}
                      className={`text-xs border-0 rounded-full px-3 py-1 font-medium cursor-pointer ${sbadge(panelTask.status)}`}>
                      {STATUS_COLS.map(s=><option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                    {panelTask.assignedTo && (
                      <div className="flex items-center gap-1 bg-gray-100 rounded-full px-2 py-1">
                        <div className="w-4 h-4 bg-purple-400 rounded-full flex items-center justify-center"><span className="text-xs text-white font-bold">{panelTask.assignedTo.name[0]}</span></div>
                        <span className="text-xs text-gray-600">{panelTask.assignedTo.name}</span>
                      </div>
                    )}
                  </div>
                  {panelTask.referenceLink && (
                    <a href={panelTask.referenceLink} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-3 py-1.5 rounded-lg w-fit">
                      <Link size={12}/> Reference Link <ExternalLink size={11}/>
                    </a>
                  )}
                  <div className="flex items-center gap-2 text-xs text-blue-600 font-semibold">
                    <Clock size={13}/> {fmtMin(panelTask.totalLoggedMinutes||0)} logged
                  </div>
                  <div className="flex gap-2 flex-wrap items-center">
                    {isManagerOrAdmin && (
                      <div className="flex items-center gap-1 border border-gray-200 rounded-lg px-2 py-1">
                        <span className="text-[10px] text-gray-500 uppercase mr-1">Points:</span>
                        {(["simple","medium","critical"] as const).map(lvl => {
                          const cur = panelTask.workLevel === lvl;
                          const meta = { simple: {c:"green", pt:1, e:"🟢"}, medium: {c:"yellow", pt:3, e:"🟡"}, critical: {c:"red", pt:5, e:"🔴"} }[lvl];
                          return (
                            <button key={lvl} disabled={classifyingStep} onClick={() => classifyStep(panelTask.id, lvl)}
                              className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${
                                cur ? `bg-${meta.c}-100 text-${meta.c}-700 border border-${meta.c}-300` : `hover:bg-${meta.c}-50 text-gray-500`
                              }`}>
                              {meta.e} {meta.pt}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {!isManagerOrAdmin && panelTask.workLevel && (
                      <span className="text-[10px] text-gray-500 border border-gray-200 rounded-lg px-2 py-1">
                        Points: {panelTask.workPoints}
                      </span>
                    )}
                    <button onClick={()=>{setEditItem(panelTask);openModal("task",panelTask);}}
                      className="flex items-center gap-1 text-xs border border-gray-200 px-2 py-1 rounded-lg hover:bg-gray-50"><Edit2 size={12}/> Edit</button>
                    <button onClick={()=>openModal("log-task")}
                      className="flex items-center gap-1 text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-lg hover:bg-blue-100"><Timer size={12}/> Log Time</button>
                    <button onClick={()=>{openModal("subtask");}}
                      className="flex items-center gap-1 text-xs bg-green-50 text-green-600 px-2 py-1 rounded-lg hover:bg-green-100"><Plus size={12}/> Sub-step</button>
                  </div>
                </div>

                {/* Task Time Logs */}
                {(panelTask.workLogs||[]).length>0 && (
                  <div className="p-4 border-b">
                    <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1"><Timer size={12}/>Time Logs</p>
                    <div className="space-y-1">
                      {(panelTask.workLogs||[]).map((l:any)=>(
                        <div key={l.id} className="flex items-center gap-2 text-xs bg-gray-50 rounded-lg px-2 py-1.5">
                          <span className="font-bold text-blue-600">{fmtMin(l.minutes||0)}</span>
                          <span className="text-gray-600">{l.employee?.name}</span>
                          <WorkLogTextDisplay comment={l.comment} description={l.description} compact />
                          <span className="text-gray-300">{format(new Date(l.date),"MMM dd")}</span>
                          <button onClick={()=>deleteLog("task",l.id)} className="text-gray-300 hover:text-red-500 flex-shrink-0"><Trash2 size={11}/></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sub-Tasks */}
                <div className="p-4 border-b">
                  <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1"><FolderKanban size={12}/>Sub-steps ({(panelTask.subTasks||[]).length})</p>
                  <div className="space-y-1">
                    {(panelTask.subTasks||[]).map((st:any)=>(
                      <div key={st.id}>
                        <div className="flex items-center gap-2 bg-gray-50 hover:bg-gray-100 rounded-lg px-2 py-2 cursor-pointer transition-colors"
                          onClick={()=>setExpandedST(expandedST===st.id?null:st.id)}>
                          {expandedST===st.id ? <ChevronDown size={14} className="text-gray-400"/> : <ChevronRight size={14} className="text-gray-400"/>}
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sdot(st.status)}`}/>
                          <span className="text-xs font-medium text-gray-700 flex-1 truncate">{st.title}</span>
                          <span className="text-xs text-gray-400">{fmtMin(st.totalLoggedMinutes||0)}</span>
                          {st.referenceLink && <Link size={11} className="text-blue-400 flex-shrink-0"/>}
                          <div className="flex gap-0.5">
                            <button onClick={e=>{e.stopPropagation();setEditItem({...st,type:"subtask"});openModal("subtask",st);}}
                              className="p-0.5 text-gray-300 hover:text-blue-500"><Edit2 size={11}/></button>
                            <button onClick={e=>{e.stopPropagation();deleteSubTask(st.id);}}
                              className="p-0.5 text-gray-300 hover:text-red-500"><Trash2 size={11}/></button>
                          </div>
                        </div>

                        {/* Sub-Task Expanded */}
                        {expandedST===st.id && (
                          <div className="ml-6 mt-1 mb-2 bg-white border border-gray-100 rounded-xl p-3 space-y-3">
                            {st.description && <p className="text-xs text-gray-500">{st.description}</p>}
                            <div className="flex flex-wrap gap-2 items-center">
                              <select value={st.status}
                                onChange={async e=>{ await axios.put(`${API}/api/tasks/subtasks/${st.id}`,{status:e.target.value},{headers:hdr()}); load(); }}
                                className={`text-xs border-0 rounded-full px-2 py-0.5 font-medium ${sbadge(st.status)}`}>
                                {STATUS_COLS.map(s=><option key={s.key} value={s.key}>{s.label}</option>)}
                              </select>
                              {st.assignedTo && <span className="text-xs text-gray-500">{st.assignedTo.name}</span>}
                            </div>
                            {st.referenceLink && (
                              <a href={st.referenceLink} target="_blank" rel="noreferrer"
                                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded-lg w-fit">
                                <Link size={11}/> Link <ExternalLink size={10}/>
                              </a>
                            )}
                            {/* SubTask time logs */}
                            {(st.workLogs||[]).length>0 && (
                              <div className="space-y-1">
                                <p className="text-xs font-semibold text-gray-400 flex items-center gap-1"><Timer size={11}/>Time Logs</p>
                                {(st.workLogs||[]).map((l:any)=>(
                                  <div key={l.id} className="flex items-center gap-1 text-xs bg-gray-50 rounded px-2 py-1">
                                    <span className="font-bold text-blue-600">{fmtMin(l.minutes||0)}</span>
                                    <span className="text-gray-500">{l.employee?.name}</span>
                                    <WorkLogTextDisplay comment={l.comment} description={l.description} compact />
                                    <span className="text-gray-300">{format(new Date(l.date),"MMM dd")}</span>
                                    <button onClick={()=>deleteLog("sub",l.id,st.id)} className="text-gray-300 hover:text-red-500"><Trash2 size={10}/></button>
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* SubTask comments */}
                            {(st.comments||[]).length>0 && (
                              <div className="space-y-1">
                                <p className="text-xs font-semibold text-gray-400">Comments</p>
                                {(st.comments||[]).map((c:any)=>(
                                  <div key={c.id} className="text-xs bg-gray-50 rounded px-2 py-1">
                                    <span className="font-semibold text-gray-700">{c.author?.name}: </span>
                                    <span className="text-gray-600">{c.content}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* Actions */}
                            <div className="flex gap-1">
                              <div className="flex-1 flex gap-1">
                                <input className="flex-1 border border-gray-200 rounded-lg px-2 py-1 text-xs"
                                  placeholder="Comment..." value={subComment}
                                  onChange={e=>setSubComment(e.target.value)}
                                  onKeyDown={e=>e.key==="Enter"&&addSubComment(st.id)}/>
                                <button onClick={()=>addSubComment(st.id)} className="bg-blue-600 text-white px-2 rounded-lg"><Send size={12}/></button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {(panelTask.subTasks||[]).length===0 && <p className="text-xs text-gray-300">No sub-tasks</p>}
                  </div>
                </div>

                {/* Task Comments */}
                <div className="p-4">
                  <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1"><MessageSquare size={12}/>Comments</p>
                  <div className="space-y-1 mb-2 max-h-32 overflow-y-auto">
                    {(panelTask.comments||[]).map((c:any)=>(
                      <div key={c.id} className="flex gap-1.5">
                        <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-blue-700">{c.author?.name?.[0]}</span>
                        </div>
                        <div className="flex-1 bg-gray-50 rounded-lg px-2 py-1.5">
                          <span className="text-xs font-semibold text-gray-700">{c.author?.name}: </span>
                          <span className="text-xs text-gray-600">{c.content}</span>
                          <span className="text-xs text-gray-300 ml-1">{format(new Date(c.createdAt),"HH:mm")}</span>
                        </div>
                      </div>
                    ))}
                    {(panelTask.comments||[]).length===0 && <p className="text-xs text-gray-300">No comments</p>}
                  </div>
                  <div className="flex gap-2">
                    <input className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Add comment..." value={taskComment}
                      onChange={e=>setTaskComment(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addTaskComment()}/>
                    <button onClick={addTaskComment} className="bg-blue-600 text-white px-3 rounded-lg hover:bg-blue-700"><Send size={14}/></button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MODALS ── */}

      {/* Task Modal */}
      {modal==="task" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{editItem?"Edit Step":"New Step"}</h2>
              <button onClick={closeModal}><X size={20}/></button>
            </div>
            <form onSubmit={submitTask} className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input required className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={taskForm.title} onChange={e=>setTaskForm({...taskForm,title:e.target.value})}/></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                  value={taskForm.description} onChange={e=>setTaskForm({...taskForm,description:e.target.value})}/></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Reference Link</label>
                <input type="url" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="https://..."
                  value={taskForm.referenceLink} onChange={e=>setTaskForm({...taskForm,referenceLink:e.target.value})}/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={taskForm.assignedToId} onChange={e=>setTaskForm({...taskForm,assignedToId:e.target.value})}>
                    <option value="">Unassigned</option>
                    {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
                  </select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={taskForm.status} onChange={e=>setTaskForm({...taskForm,status:e.target.value})}>
                    {STATUS_COLS.map(s=><option key={s.key} value={s.key}>{s.label}</option>)}
                  </select></div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={closeModal} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">{editItem?"Update":"Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* SubTask Modal */}
      {modal==="subtask" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{editItem?.type==="subtask"?"Edit Sub-step":"New Sub-step"}</h2>
              <button onClick={closeModal}><X size={20}/></button>
            </div>
            <form onSubmit={submitSubTask} className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input required className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={subForm.title} onChange={e=>setSubForm({...subForm,title:e.target.value})}/></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                  value={subForm.description} onChange={e=>setSubForm({...subForm,description:e.target.value})}/></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Reference Link</label>
                <input type="url" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="https://..."
                  value={subForm.referenceLink} onChange={e=>setSubForm({...subForm,referenceLink:e.target.value})}/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={subForm.assignedToId} onChange={e=>setSubForm({...subForm,assignedToId:e.target.value})}>
                    <option value="">Unassigned</option>
                    {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
                  </select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={subForm.status} onChange={e=>setSubForm({...subForm,status:e.target.value})}>
                    {STATUS_COLS.map(s=><option key={s.key} value={s.key}>{s.label}</option>)}
                  </select></div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={closeModal} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">{editItem?.type==="subtask"?"Update":"Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Log Time Modal */}
      {(modal==="log-proj"||modal==="log-task"||modal==="log-sub") && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Log Time{modal==="log-proj"?" — Project":modal==="log-task"?" — Step":` — Sub-step`}</h2>
              <button onClick={closeModal}><X size={20}/></button>
            </div>
            <form onSubmit={submitLog} className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Time Logged *</label>
                <div className="flex gap-2">
                  <input required type="text"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder={(logForm.unit||"min")==="hr" ? "e.g. 1.5" : "e.g. 30"}
                    value={logForm.minutes} onChange={e=>setLogForm(f=>({...f,minutes:e.target.value}))}/>
                  <div className="flex border border-gray-300 rounded-lg overflow-hidden text-sm">
                    <button type="button" onClick={()=>setLogForm(f=>({...f,unit:"min"}))}
                      className={`px-3 py-2 font-medium transition-colors ${(logForm.unit||"min")==="min"?"bg-blue-600 text-white":"bg-white text-gray-600 hover:bg-gray-50"}`}>min</button>
                    <button type="button" onClick={()=>setLogForm(f=>({...f,unit:"hr"}))}
                      className={`px-3 py-2 font-medium transition-colors ${logForm.unit==="hr"?"bg-blue-600 text-white":"bg-white text-gray-600 hover:bg-gray-50"}`}>hr</button>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1">Also accepts: 1h 30m · 1.5 · 90</p>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input required type="date" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={logForm.date} onChange={e=>setLogForm({...logForm,date:e.target.value})}/></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Comment</label>
                <textarea rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                  placeholder="What did you work on?"
                  value={logForm.comment} onChange={e=>setLogForm({...logForm,comment:e.target.value})}/></div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={closeModal} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">Log Time</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
